import alt from 'components/Dispatcher';
import keyBy from 'lodash/keyBy';
import TeamActions from 'actions/TeamActions';
import {List, Map} from 'immutable';
import DepartmentStore from 'stores/department/DepartmentStore';
import ToastStore from '@store/toast/ToastStore';
import Department, {DepartmentId} from 'records/Department';
import Team from 'records/Team';
import User from 'records/User';
import {Store} from '@store/@types/alt';
import {TeamId} from 'records/TeamId';
import {associateBy} from 'utils/associateBy';

interface TeamListStoreState {
  departmentStagedToReceive: Department | null;
  departmentStagedToRemoveFrom: Department | null;
  departmentTeams: Map<DepartmentId, List<Team>>;
  departmentTeamErrors: Map<DepartmentId, boolean>;
  departmentTeamsLoading: Map<DepartmentId, boolean>;
  teamStagedForMove: Team | null;
  users: Map<TeamId, List<User>>;
  usersLoading: Map<TeamId, boolean>;
  isTeamSaving: boolean;
  teams: Map<TeamId, Team>;
  // Used for transforming team_id queries into department_id queries
  teamDepartments: Map<TeamId, DepartmentId>;
}

const errorMessage = operation =>
  `There was a problem communicating with the server when ${operation} the team.`;

const without = team => otherTeam => team !== otherTeam;

class TeamListStore extends Store<TeamListStoreState> {
  constructor() {
    super();
    this.state = {
      departmentStagedToReceive: null,
      departmentStagedToRemoveFrom: null,
      departmentTeamErrors: Map(),
      departmentTeams: Map(),
      departmentTeamsLoading: Map(),
      teamStagedForMove: null,
      users: Map(),
      usersLoading: Map(),
      isTeamSaving: false,
      teams: Map(),
      teamDepartments: Map(),
    };
    this.bindActions(TeamActions);
  }

  fetchTeamsForDepartment(departmentId) {
    this.setState({
      departmentTeamErrors: this.state.departmentTeamErrors.set(departmentId, false),
      departmentTeamsLoading: this.state.departmentTeamsLoading.set(departmentId, true),
      departmentTeams: this.state.departmentTeams.set(departmentId, List()),
    });
  }

  fetchTeamsForDepartmentSuccess([departmentId, teams]) {
    const stateTeams = this.state.teams;
    const teamArray = teams.toArray();
    const keyedTeams = keyBy(teamArray, item => item.id);
    this.setState({
      departmentTeamsLoading: this.state.departmentTeamsLoading.set(departmentId, false),
      departmentTeams: this.state.departmentTeams.set(departmentId, teams),
      teams: stateTeams.merge(keyedTeams),
    });
  }

  fetchTeamsForDepartmentError(departmentId) {
    this.setState({
      departmentTeamErrors: this.state.departmentTeamErrors.set(departmentId, true),
      departmentTeamsLoading: this.state.departmentTeamsLoading.set(departmentId, false),
    });
  }

  fetchTeamsForUserSuccess(teams) {
    const stateTeams = this.state.teams;
    const teamArray = teams.toArray();
    const keyedTeams = keyBy(teamArray, item => item.id);
    const departmentTeams = teams.groupBy(it => it.departmentId);
    this.setState({
      departmentTeams: departmentTeams,
      teams: stateTeams.merge(keyedTeams),
    });
  }

  stageTeamForMove(team) {
    const {departmentProfiles} = DepartmentStore.getState();
    const department = departmentProfiles.find(({id}) => id === team.departmentId);
    this.setState({
      teamStagedForMove: team,
      departmentStagedToReceive: department,
      departmentStagedToRemoveFrom: department,
    });
  }

  stageDepartmentToReceive(department) {
    this.setState({departmentStagedToReceive: department});
  }

  discardTeamMove() {
    this.setState({
      departmentStagedToReceive: null,
      departmentStagedToRemoveFrom: null,
      teamStagedForMove: null,
    });
  }

  updateTeamSuccess(updatedTeam) {
    const {departmentTeams} = this.state;
    // code here seems pretty sure this find wont return undefined so
    const originalTeam = departmentTeams
      .toList()
      .map(teams => teams.find(team => team.id === updatedTeam.id))
      .find(team => !!team) as Team;
    if (updatedTeam.departmentId !== originalTeam.departmentId) {
      const removeTeamList = departmentTeams
        .get(originalTeam.departmentId, List())
        .filter(without(originalTeam));
      const receiveTeamList = departmentTeams
        .get(updatedTeam.departmentId, List())
        .push(updatedTeam);
      this.setState({
        isTeamSaving: false,
        departmentTeams: departmentTeams
          .set(updatedTeam.departmentId, receiveTeamList)
          .set(originalTeam.departmentId, removeTeamList),
      });
    } else {
      const teamList = departmentTeams.get(originalTeam.departmentId, List());
      const index = teamList.indexOf(originalTeam);
      this.setState({
        isTeamSaving: false,
        departmentTeams: departmentTeams.set(
          originalTeam.departmentId,
          teamList.splice(index, 1, updatedTeam)
        ),
      });
    }
    ToastStore.addToast({
      type: 'success',
      time: 2000,
      message: 'Team updated!',
    });
  }

  updateTeamError() {
    this.setState({
      isTeamSaving: false,
    });
    ToastStore.addToast({
      type: 'error',
      message: errorMessage('updating'),
    });
  }

  deleteTeamSuccess(teamId) {
    const {departmentTeams} = this.state;
    const team = this.state.teams.get(teamId);
    if (team) {
      const teamList = departmentTeams.get(team.departmentId, List());
      this.setState({
        departmentTeams: this.state.departmentTeams.set(
          team.departmentId,
          teamList.delete(teamList.indexOf(team))
        ),
        teams: this.state.teams.delete(team.id),
      });
      ToastStore.addToast({
        type: 'success',
        time: 2000,
        message: 'Empty Team deleted!',
      });
    }
  }

  deleteTeamError() {
    ToastStore.addToast({
      type: 'error',
      message: errorMessage('deleting'),
    });
  }

  createTeam() {
    this.setState({
      isTeamSaving: true,
    });
  }

  updateTeam() {
    this.setState({
      isTeamSaving: true,
    });
  }

  createTeamSuccess(team) {
    const {departmentTeams, teams} = this.state;
    const teamList = departmentTeams.get(team.departmentId, List());
    this.setState({
      isTeamSaving: false,
      departmentTeams: departmentTeams.set(team.departmentId, teamList.push(team)),
      teams: teams.set(team.id, team),
    });
    ToastStore.addToast({
      type: 'success',
      message: `Team "${team.name}" was created!`,
    });
  }

  createTeamError() {
    this.setState({
      isTeamSaving: false,
    });
    ToastStore.addToast({
      type: 'error',
      message: errorMessage('creating'),
    });
  }

  fetchUsersLoading(teamIds) {
    const usersLoadingMerge = {};
    teamIds.forEach(teamId => (usersLoadingMerge[teamId] = true));
    this.setState({
      // We don't have removeAll :(
      users: this.state.users.withMutations(map => teamIds.forEach(teamId => map.remove(teamId))),
      usersLoading: this.state.usersLoading.merge(usersLoadingMerge),
    });
  }

  fetchUsersSuccess(users: List<User>) {
    const teamUsers = users.groupBy(u => u.teamId);
    const teamIds = teamUsers.keySeq();
    this.setState({
      users: this.state.users.merge(teamUsers),
      // We don't have removeAll :(
      usersLoading: this.state.usersLoading.withMutations(map =>
        teamIds.forEach(teamId => map.remove(teamId))
      ),
    });
  }
  fetchUsersError(error) {
    console.error(error);
  }

  fetchAllTeamsForAdmin(): void {}
  fetchAllTeamsForAdminError(error): void {
    console.error(error);
  }
  fetchAllTeamsForAdminSuccess(teams: List<Team>): void {
    this.setState({teams: associateBy(teams, t => t.id)});
  }

  fetchTeamDepartments(): void {}
  fetchTeamDepartmentsError(error): void {
    console.error(error);
  }
  fetchTeamDepartmentsSuccess(teamDepartments: Map<TeamId, DepartmentId>): void {
    this.setState({teamDepartments});
  }
}

export default alt.createStore(TeamListStore);
